import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { Login } from './screens/Login'
import { initTelegram, inTelegram, tg } from './lib/telegram'
import { ensureSession, isAuthed } from './lib/auth'
import { startVersionWatch } from './lib/version'
import { watchViewportHeight } from './lib/viewport'
import { SirrProvider } from './store'
import { hasStoredProfile, loadProfile } from './lib/storage'
import { startAnalytics, track } from './lib/analytics'
import './theme.css'

initTelegram()
startAnalytics()
// Следим за версией сборки: iOS-PWA сам обновления не подтягивает, поэтому при
// новой сборке мягко перезагружаемся (на запуске и при возврате на экран).
startVersionWatch()
// Высоту каркаса меряем до первой отрисовки: иначе панель прыгает.
watchViewportHeight()
// Язык берём тот, что человек выбрал в приложении, а не настройку Telegram:
// иначе отчёт рассказывает про языки телефонов, а не про то, на каком языке
// люди на самом деле гадают. Настройка Telegram — лишь первое предположение
// при самом первом запуске.
track('app_open', {
  platform: tg?.platform ?? 'browser',
  lang: hasStoredProfile() ? loadProfile().lang : 'az',
})

/**
 * Корень приложения.
 *
 * **Стены входа здесь больше нет.** Раньше в браузере без сессии показывался
 * экран входа вместо приложения: человек отдавал аккаунт Google раньше, чем
 * видел первую карту. Замерено на боевом — за 15 дней 1333 адреса открыли
 * `sirr.app`, вход начали четверо. Плюс правило App Store 5.1.1(v) прямо
 * запрещает требовать регистрацию, когда аккаунт не нужен для основной работы.
 *
 * Теперь сессия заводится сама и молча (анонимная), а вход стал предложением —
 * его показывает профиль и пейволл. В Telegram ничего не изменилось: там
 * человек опознан подписью, и `isAuthed()` истинно всегда.
 *
 * Пока сессии нет, не рисуем НИЧЕГО: приложение без неё всё равно не сделает
 * ни одного запроса, а мелькнувший и пропавший экран хуже, чем полсекунды
 * пустоты. Если завести сессию не вышло (нет сети), показываем вход — там есть
 * и объяснение, и кнопки, то есть человек не остаётся перед пустотой.
 */
function Root() {
  const [ready, setReady] = useState(isAuthed)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    // Сессия истекла на лету (сервер ответил 401) — заводим новую. Для
    // анонима это новый чистый аккаунт, но выбора всё равно нет: токен был
    // единственной ниточкой к прежнему.
    const onExpired = () => { setReady(false); void start() }
    const onAuthed = () => { setFailed(false); setReady(true) }
    window.addEventListener('sirr-auth-expired', onExpired)
    window.addEventListener('sirr-authed', onAuthed)
    return () => {
      window.removeEventListener('sirr-auth-expired', onExpired)
      window.removeEventListener('sirr-authed', onAuthed)
    }
  }, [])

  async function start() {
    try {
      await ensureSession(hasStoredProfile() ? loadProfile().lang : 'az')
      setFailed(false)
      setReady(true)
    } catch {
      setFailed(true)
    }
  }

  useEffect(() => {
    if (!isAuthed()) void start()
  }, [])

  if (failed) return <Login />
  if (!ready) return null
  return (
    <SirrProvider>
      <App />
    </SirrProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
)

// Service worker нужен только PWA-версии — ради установки «на экран» и
// оффлайн-заглушки. В Telegram он не нужен, поэтому там не регистрируем.
if (!inTelegram && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* без SW приложение всё равно работает, просто не предложит установку */
    })
  })
}
